import React, { Component } from 'react';
import PropTypes from 'prop-types';
import lodashSortBy from 'lodash/fp/sortBy';

import { SR_API_ACTION_ADMIN_GET_TOURS_BY_USER_ID } from 'api/actionTypes';
import {
  DATA_TABLE_SOURCE_ADMIN_TOURS,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_NEEDS_RESCHEDULING,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_REQUESTED,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_REQUESTED_RECENTLY_ADDED,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_SCHEDULED,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_SCHEDULED_ADDED_TO_TOUR,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_SCHEDULED_UNASSIGNED,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_SCHEDULED_UNASSIGNED_INDICATOR,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_COMPLETED,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_COMPLETED_QUESTION_UNASKED,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_COMPLETED_QUESTION_UNANSWERED,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_ALL,
} from 'common/dataTableConstants';

import AdminActions from 'client/actions/Admin';
import postWithState from 'client/actions/postWithState';
import branch from 'client/core/branch';

import SrAdminTourCard from 'client/components/SrAdminTourCard';
import SrAdminToursRequestTourAsAUserModal from 'client/components/SrAdminToursRequestTourAsAUserModal';
import SrAdminToursAddToTourList from 'client/components/SrAdminToursAddToTourList';
import { SR_USER_SETTING_LISTING_MANAGEMENT_COMPANY_FILTER_BY_ALL } from 'client/components/SrUserSettingListingManagementCompanyDropdown';
import withInfiniteScroll from 'client/helpers/withInfiniteScroll';

import {
  dataTable,
  actionDataTableLoadNextPage,
  actionDataTableReset,
} from 'client/helpers/dataTables';

import {
  TOUR_STATUS_REQUESTED,
  TOUR_STATUS_SCHEDULED,
  TOUR_STATUS_COMPLETED,
} from 'common/tourConstants';

const dataTableViewWithCustomSort = [
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_NEEDS_RESCHEDULING,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_REQUESTED,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_REQUESTED_RECENTLY_ADDED,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_SCHEDULED,
  DATA_TABLE_VIEW_ID_ADMIN_TOURS_SCHEDULED_ADDED_TO_TOUR,
];

function getDataTableSettingsFromProps(props) {
  let dataTableSettings = {};
  switch (props.viewId) {
    case DATA_TABLE_VIEW_ID_ADMIN_TOURS_NEEDS_RESCHEDULING: {
      dataTableSettings = {
        sort: {
          'tours.startDate': 'asc',
        },
        filter: {
          'tours.status': TOUR_STATUS_REQUESTED,
          'tours.markedNeedsReschedulingFlag': true,
        },
      };
      break;
    }
    case DATA_TABLE_VIEW_ID_ADMIN_TOURS_REQUESTED: {
      dataTableSettings = {
        sort: {
          'tours.startDate': 'asc',
        },
        filter: {
          'tours.status': TOUR_STATUS_REQUESTED,
          'tours.markedNeedsReschedulingFlag': false,
        },
      };
      break;
    }
    case DATA_TABLE_VIEW_ID_ADMIN_TOURS_REQUESTED_RECENTLY_ADDED: {
      dataTableSettings = {
        sort: {
          'tours.startDate': 'asc',
        },
        filter: {
          'tours.status': TOUR_STATUS_REQUESTED,
          'tours.markedNeedsReschedulingFlag': false,
        },
        customParams: {
          tourDashboard: {
            unread: true,
          },
        },
      };
      break;
    }
    case DATA_TABLE_VIEW_ID_ADMIN_TOURS_SCHEDULED: {
      dataTableSettings = {
        sort: {
          'tours.startDate': 'asc',
        },
        filter: {
          'tours.status': TOUR_STATUS_SCHEDULED,
        },
      };
      break;
    }
    case DATA_TABLE_VIEW_ID_ADMIN_TOURS_SCHEDULED_ADDED_TO_TOUR: {
      dataTableSettings = {
        sort: {
          'tours.startDate': 'asc',
        },
        filter: {
          'tours.status': TOUR_STATUS_SCHEDULED,
        },
        customParams: {
          tourDashboard: {
            unread: true,
          },
        },
      };
      break;
    }
    case DATA_TABLE_VIEW_ID_ADMIN_TOURS_SCHEDULED_UNASSIGNED: {
      dataTableSettings = {
        sort: {
          'tours.startDate': 'asc',
        },
        filter: {
          'tours.status': TOUR_STATUS_SCHEDULED,
          'tours.agentUserId': null,
        },
      };
      break;
    }
    case DATA_TABLE_VIEW_ID_ADMIN_TOURS_SCHEDULED_UNASSIGNED_INDICATOR: {
      dataTableSettings = {
        count: 1,
        sort: {
          'tours.startDate': 'asc',
        },
        filter: {
          'tours.status': TOUR_STATUS_SCHEDULED,
          'tours.agentUserId': null,
        },
        customParams: {
          tourDashboard: {
            unassigned: true,
          },
        },
      };
      break;
    }
    case DATA_TABLE_VIEW_ID_ADMIN_TOURS_COMPLETED: {
      dataTableSettings = {
        sort: {
          'tours.lastStatusUpdateDate': 'desc',
        },
        filter: {
          'tours.status': TOUR_STATUS_COMPLETED,
        },
      };
      break;
    }
    case DATA_TABLE_VIEW_ID_ADMIN_TOURS_COMPLETED_QUESTION_UNASKED: {
      dataTableSettings = {
        sort: {
          'tours.lastStatusUpdateDate': 'desc',
        },
        filter: {
          'tours.status': TOUR_STATUS_COMPLETED,
        },
        customParams: {
          tourDashboard: {
            unaskedQuestion: true,
          },
        },
      };
      break;
    }
    case DATA_TABLE_VIEW_ID_ADMIN_TOURS_COMPLETED_QUESTION_UNANSWERED: {
      dataTableSettings = {
        sort: {
          'tours.lastStatusUpdateDate': 'desc',
        },
        filter: {
          'tours.status': TOUR_STATUS_COMPLETED,
        },
        customParams: {
          tourDashboard: {
            unansweredQuestion: true,
          },
        },
      };
      break;
    }
    case DATA_TABLE_VIEW_ID_ADMIN_TOURS_ALL: {
      dataTableSettings = {
        sort: {
          'tours.lastStatusUpdateDate': 'desc',
        },
        filter: {},
      };
      break;
    }
  }

  if (props.sortBy && dataTableViewWithCustomSort.includes(props.viewId)) {
    const order = props.sortBy === 'lastStatusUpdateDate' ? 'desc' : 'asc';
    dataTableSettings.viewId = `${props.viewId}-WITH-SORT-${props.sortBy}`;
    dataTableSettings.sort = {
      [`tours.${props.sortBy}`]: order,
    };
  }
  if (props.marketId) {
    dataTableSettings.viewId = `${dataTableSettings.viewId}-WITH-MARKET-${props.marketId}`;
    dataTableSettings.customParams = dataTableSettings.customParams || { tourDashboard: {} };
    dataTableSettings.customParams.tourDashboard.marketId = props.marketId;
  }
  if (props.listingManagementCompany !== undefined) {
    dataTableSettings.viewId = `${dataTableSettings.viewId}-WITH-MANAGEMENT_COMPANY-${props.listingManagementCompany}`; // eslint-disable-line max-len
    dataTableSettings.customParams = dataTableSettings.customParams || { tourDashboard: {} };
    dataTableSettings.customParams.tourDashboard.listingManagementCompany =
      props.listingManagementCompany === SR_USER_SETTING_LISTING_MANAGEMENT_COMPANY_FILTER_BY_ALL // eslint-disable-line max-len
        ? props.partnersList.map(({ managementCompanyName }) => managementCompanyName)
        : props.listingManagementCompany;
  }
  if (props.userId) {
    dataTableSettings.viewId = `${props.viewId}-BY-USER-ID-${props.userId}`;
    dataTableSettings.filter = {
      'tours.userId': parseInt(props.userId, 10),
    };
    dataTableSettings.customParams = { tourDashboard: {} };
  }
  return dataTableSettings;
}

@dataTable((props) => ({
  dataSource: DATA_TABLE_SOURCE_ADMIN_TOURS,
  viewId: props.viewId,
  ...getDataTableSettingsFromProps(props),
}))
@withInfiniteScroll({
  offset: 32,
})
@branch({
  appUrl: ['env', 'APP_URL'],
})
class SrDataTableAdminTours extends Component {
  static propTypes = {
    items: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
    dispatch: PropTypes.func.isRequired,
    dataTableApiState: PropTypes.object.isRequired,
    dataTableIsFullyLoaded: PropTypes.bool.isRequired,
    onInfiniteScrollWrapperRef: PropTypes.func.isRequired,
    appUrl: PropTypes.string.isRequired,
    partnersList: PropTypes.array, // eslint-disable-line react/no-unused-prop-types
  };

  constructor(props) {
    super(props);

    this.state = {
      requestTourIsOpen: false,
      addToTourIsOpen: false,
      tourListingToBeAdded: {
        listingId: null,
        tour: null,
        userTours: null,
        markNeedsRescheduling: false,
      },
    };

    const { onInfiniteScrollCallbackReady } = props;
    onInfiniteScrollCallbackReady(this._infiniteScrollCallback);
  }

  _infiniteScrollCallback = () => {
    const { dataTableIsFullyLoaded, dispatch, viewId } = this.props;
    if (!dataTableIsFullyLoaded) {
      dispatch(actionDataTableLoadNextPage, {
        dataSource: DATA_TABLE_SOURCE_ADMIN_TOURS,
        viewId: viewId,
        count: 8,
      });
    }
  };

  _onRefreshButtonClick = () => {
    const { dispatch } = this.props;
    dispatch(actionDataTableReset, {
      dataSource: DATA_TABLE_SOURCE_ADMIN_TOURS,
    }).then(() => {
      dispatch(AdminActions.getItemsForToursIndicators);
    });
  };

  _requestTourAsUserToggle = () => {
    this.setState({
      requestTourIsOpen: !this.state.requestTourIsOpen,
    });
  };

  _addToTour = ({ listingId, tour }) => {
    if (!this.state.addToTourIsOpen) {
      this.props
        .dispatch(postWithState, {
          action: SR_API_ACTION_ADMIN_GET_TOURS_BY_USER_ID,
          payload: {
            userId: tour.userId,
          },
        })
        .then((userTours) => {
          const scheduledUserTours = userTours.filter((tour) => tour.status === 'scheduled');

          this.setState({
            addToTourIsOpen: !this.state.addToTourIsOpen,
            tourListingToBeAdded: {
              listingId,
              tour,
              userTours: scheduledUserTours,
            },
          });
        });
    } else {
      this.setState({
        addToTourIsOpen: !this.state.addToTourIsOpen,
      });
    }
  };

  _addToTourClose = () => {
    this.setState({
      addToTourIsOpen: false,
    });
  };

  render() {
    const { items, dispatch, dataTableApiState, onInfiniteScrollWrapperRef, appUrl } = this.props;

    const { tourListingToBeAdded } = this.state;

    return (
      <div ref={onInfiniteScrollWrapperRef}>
        <button
          className="floatButton refresh"
          type="button"
          onClick={this._onRefreshButtonClick}
        />
        <button className="floatButton add" type="button" onClick={this._requestTourAsUserToggle} />
        <SrAdminToursRequestTourAsAUserModal
          isOpen={this.state.requestTourIsOpen}
          onClose={this._requestTourAsUserToggle}
        />
        <SrAdminToursAddToTourList
          isOpen={this.state.addToTourIsOpen}
          onClose={this._addToTourClose}
          tourListingToBeAdded={tourListingToBeAdded}
        />
        {items.map((tour) => {
          // WARN: Tour listings are not naturally sorted in the backend. Sorting them here.
          const sortedShowings = lodashSortBy(['position'], tour.showings);
          const sortedListings = sortedShowings.map(({ listingId }) => {
            return tour.listings.find(({ id }) => id === listingId);
          });
          const tourWithSortedListings = {
            ...tour,
            showings: sortedShowings,
            listings: sortedListings,
          };
          return (
            <SrAdminTourCard
              key={tour.id}
              tour={tourWithSortedListings}
              dispatch={dispatch}
              addToTour={this._addToTour}
              appUrl={appUrl}
            />
          );
        })}
        {dataTableApiState.inProgress && <div className="loading-ellipses left" />}
        {dataTableApiState.error && <span>ERROR: {dataTableApiState.error}</span>}
      </div>
    );
  }
}

SrDataTableAdminTours.displayName = 'SrDataTableAdminTours';

export default SrDataTableAdminTours;
